import { useId } from "react";

import { FigureFrame } from "./FigureFrame";
import type { ResearchFigureProps } from "./types";

const ink = "var(--figure-ink, currentColor)";
const accent = "var(--figure-accent, currentColor)";
const surface = "var(--figure-surface, transparent)";

export function PushFigure({
  className,
  caption,
  title = "Personalized push notifications in a physical activity app",
  description = "A field experiment with 595 users compares generic, pet-name, and owner-plus-pet-name messages. Greater personalization performs better, and reference to past activity reinforces the effect.",
}: ResearchFigureProps) {
  const markerId = `push-arrow-${useId().replace(/:/g, "")}`;

  return (
    <FigureFrame
      className={className}
      caption={caption}
      title={title}
      description={description}
      viewBox="0 0 760 420"
    >
      <defs>
        <marker
          id={markerId}
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill={accent} />
        </marker>
      </defs>

      <text x="32" y="36" fill={ink} fontSize="12" fontWeight="650" letterSpacing="1.6">
        FIELD EXPERIMENT · N = 595
      </text>
      <line x1="32" y1="54" x2="728" y2="54" stroke={ink} strokeWidth="1.25" />

      <g fill={surface} stroke={ink} strokeWidth="1.25">
        <rect x="32" y="94" width="192" height="70" />
        <rect x="284" y="94" width="192" height="70" />
        <rect x="536" y="94" width="192" height="70" />
      </g>
      <g fill={ink} fontSize="15" textAnchor="middle">
        <text x="128" y="123" fontWeight="650">Generic</text>
        <text x="128" y="147" fontSize="12">activity reminder</text>
        <text x="380" y="123" fontWeight="650">Pet name</text>
        <text x="380" y="147" fontSize="12">personal cue</text>
        <text x="632" y="123" fontWeight="650">Owner + pet</text>
        <text x="632" y="147" fontSize="12">joint identity cue</text>
      </g>

      <path
        d="M 56 209 H 702"
        fill="none"
        stroke={accent}
        strokeWidth="1.5"
        markerEnd={`url(#${markerId})`}
      />
      <text x="32" y="214" fill={ink} fontSize="12" textAnchor="start">
        less personalized
      </text>
      <text x="728" y="214" fill={accent} fontSize="12" fontWeight="700" textAnchor="end">
        stronger response
      </text>

      <g transform="translate(32 276)">
        <line x1="0" y1="0" x2="696" y2="0" stroke={ink} strokeOpacity=".25" />
        <text x="0" y="36" fill={ink} fontSize="12" fontWeight="650" letterSpacing="1.6">
          CONTEXT
        </text>
        <text x="0" y="76" fill={ink} fontSize="19" fontWeight="650">
          Past activity reference
        </text>
        <path
          d="M 279 70 H 440"
          stroke={accent}
          strokeWidth="1.5"
          markerEnd={`url(#${markerId})`}
        />
        <text x="468" y="76" fill={accent} fontSize="16" fontWeight="700">
          reinforces personalization
        </text>
      </g>
    </FigureFrame>
  );
}
